/**
 * Pricing taxonomy → productData taxonomy alias map.
 *
 * The pricing engine catalog (sourced from the StickerYou xlsx) and
 * the wizard's productData (mockData.js) use overlapping but
 * non-identical naming conventions. The Pricing Catalog "Add to Cart
 * & Continue" deep-link forwards `category|format|finish` display
 * names; the wizard's `parseQuoteParams` resolves them to wizard IDs.
 *
 * For ~80% of combos, a case-insensitive substring match is enough
 * (e.g. pricing "Static Clings" → productData "Static Clings (white
 * or clear)"). The remaining ~20% have legitimately different
 * organizing principles — e.g. pricing groups all rolls under one
 * "Rolls" format while productData splits rolls by substrate
 * (`clear-bopp-rolls`, `white-bopp-rolls`, `semigloss-paper`, ...).
 *
 * iter114s.52 — This explicit alias table covers every (category,
 * format, finish) combo currently in `/api/pricing/options` so 100%
 * of catalog Continue clicks land directly on the size step in the
 * wizard. Falls back to the heuristic resolver in `parseQuoteParams`
 * for any combo not listed here (e.g. when admin imports new
 * xlsx-pricing rows that haven't been aliased yet).
 *
 * The key is `${category}|${format}|${finish}` (case-insensitive
 * before lookup). Values are the wizard's `(productId, formatId,
 * materialId)` triple. Empty string means "leave unset and let the
 * heuristic try".
 */
const ALIASES = {
  // ============================================================
  // Decals & Clings (7)
  // ============================================================
  'Decals & Clings|Floor & Outdoor|White Vinyl - Smooth Surface Removable Glossy': {
    product: 'decals',
    format: 'indoor-floor-decals',
    material: 'indoor-floor-decals-semi-gloss-permanent',
  },
  'Decals & Clings|Static Clings|Clear Static Removable Glossy': {
    product: 'decals',
    format: 'static-clings',
    material: 'clear-static-clings-kiss-cut',
  },
  'Decals & Clings|Static Clings|White Static Removable Glossy': {
    product: 'decals',
    format: 'static-clings',
    material: 'white-static-clings-kiss-cut',
  },
  'Decals & Clings|Transfers|Vinyl Lettering Removable Glossy': {
    product: 'decals',
    format: 'vinyl-graphics',
    material: 'vinyl-graphics-white-vinyl-removable',
  },
  'Decals & Clings|Wall & Window|Crystal Clear Vinyl Removable Glossy': {
    product: 'decals',
    format: 'clear-decals',
    material: 'clear-decals-crystal-clear-glossy',
  },
  'Decals & Clings|Wall & Window|Vinyl Lettering Removable Glossy': {
    product: 'decals',
    format: 'vinyl-graphics',
    material: 'vinyl-graphics-white-vinyl-removable',
  },
  'Decals & Clings|Wall & Window|White Vinyl Removable Matte': {
    product: 'decals',
    format: 'wall-decals',
    material: 'wall-decals-white-vinyl-removable',
  },

  // ============================================================
  // Labels (17) — pricing splits by Page/Roll, productData splits
  // pages by material, rolls by substrate.
  // ============================================================
  // Page (Multiple Designs) — productData has only `white-vinyl-pages`
  // for the page format, so all 5 finishes route there with the
  // closest material.
  'Labels|Page (Multiple Designs)|Crystal Clear Vinyl Removable Glossy': {
    product: 'labels',
    format: 'white-vinyl-pages',
    material: 'white-vinyl-removable-glossy',
  },
  'Labels|Page (Multiple Designs)|Eco-Safe PET Sticker Pages': {
    product: 'labels',
    format: 'white-vinyl-pages',
    material: 'white-vinyl-removable-glossy',
  },
  'Labels|Page (Multiple Designs)|White Vinyl Permanent Glossy': {
    product: 'labels',
    format: 'white-vinyl-pages',
    material: 'white-vinyl-permanent-glossy',
  },
  'Labels|Page (Multiple Designs)|White Vinyl Removable Glossy': {
    product: 'labels',
    format: 'white-vinyl-pages',
    material: 'white-vinyl-removable-glossy',
  },
  'Labels|Page (Multiple Designs)|White Vinyl Removable Matte': {
    product: 'labels',
    format: 'white-vinyl-pages',
    material: 'white-vinyl-removable-matte',
  },
  // Page (Single Design) — same target subcategory, different
  // upstream pricing curve.
  'Labels|Page (Single Design)|Crystal Clear Vinyl Removable Glossy': {
    product: 'labels',
    format: 'white-vinyl-pages',
    material: 'white-vinyl-removable-glossy',
  },
  'Labels|Page (Single Design)|Eco-Safe PET Sticker Pages': {
    product: 'labels',
    format: 'white-vinyl-pages',
    material: 'white-vinyl-removable-glossy',
  },
  'Labels|Page (Single Design)|White Vinyl Permanent Glossy': {
    product: 'labels',
    format: 'white-vinyl-pages',
    material: 'white-vinyl-permanent-glossy',
  },
  'Labels|Page (Single Design)|White Vinyl Removable Glossy': {
    product: 'labels',
    format: 'white-vinyl-pages',
    material: 'white-vinyl-removable-glossy',
  },
  'Labels|Page (Single Design)|White Vinyl Removable Matte': {
    product: 'labels',
    format: 'white-vinyl-pages',
    material: 'white-vinyl-removable-matte',
  },
  // Rolls — substrate-specific routing.
  'Labels|Rolls|Clear BOPP Permanent Glossy': {
    product: 'labels',
    format: 'clear-bopp-rolls',
    material: 'clear-bopp-permanent-glossy',
  },
  'Labels|Rolls|Silver Foil Permanent Metallic Silver': {
    product: 'labels',
    format: 'gold-silver-foil',
    material: 'colored-foil-permanent-shiny',
  },
  'Labels|Rolls|White BOPP Permanent Glossy (Laminated)': {
    product: 'labels',
    format: 'white-bopp-rolls',
    material: 'white-bopp-permanent-glossy-laminated',
  },
  'Labels|Rolls|White BOPP Permanent Matte (Writeable)': {
    product: 'labels',
    format: 'writable-bopp',
    material: 'white-bopp-permanent-matte-writeable',
  },
  'Labels|Rolls|White Paper Permanent Glossy (Laminated)': {
    product: 'labels',
    format: 'white-bopp-rolls',
    material: 'white-paper-permanent-glossy-laminated',
  },
  'Labels|Rolls|White Paper Permanent Semi-Gloss (Eco-Safe)': {
    product: 'labels',
    format: 'semigloss-paper',
    material: 'white-paper-permanent-semi-gloss-eco',
  },
  'Labels|Rolls|White Paper Removable Glossy (Laminated)': {
    product: 'labels',
    format: 'semigloss-paper',
    material: 'white-paper-permanent-semi-gloss-eco',
  },

  // ============================================================
  // Stickers (9) — pricing has only Die-Cut + Kiss-Cut, productData
  // has 6 subcategories. Route by finish-substrate first.
  // ============================================================
  'Stickers|Die-Cut|Clear Vinyl Removable Semi-Gloss': {
    product: 'stickers',
    format: 'transparent-stickers',
    material: 'clear-vinyl-removable-semi',
  },
  'Stickers|Die-Cut|Glow In The Dark Permanent Stickers': {
    product: 'stickers',
    format: 'die-cut-singles',
    material: 'white-vinyl-permanent-semi',
  },
  'Stickers|Die-Cut|Permanent Glitter Permanent Glossy': {
    product: 'stickers',
    format: 'glitter-stickers',
    material: 'permanent-glitter-glossy',
  },
  'Stickers|Die-Cut|Permanent Holographic Permanent Glossy': {
    product: 'stickers',
    format: 'holographic-stickers',
    material: 'permanent-holographic-glossy',
  },
  'Stickers|Die-Cut|White Vinyl Permanent Semi-Gloss': {
    product: 'stickers',
    format: 'die-cut-singles',
    material: 'white-vinyl-permanent-semi',
  },
  'Stickers|Die-Cut|White Vinyl Removable Semi-Gloss': {
    product: 'stickers',
    format: 'die-cut-singles',
    material: 'white-vinyl-removable-semi',
  },
  'Stickers|Kiss-Cut|Crystal Clear Vinyl Removable Glossy': {
    product: 'stickers',
    format: 'transparent-stickers',
    material: 'clear-vinyl-removable-semi',
  },
  'Stickers|Kiss-Cut|White Vinyl Removable Glossy': {
    product: 'stickers',
    format: 'custom-stickers',
    material: 'white-vinyl-removable-semi',
  },
  'Stickers|Kiss-Cut|White Vinyl Removable Matte': {
    product: 'stickers',
    format: 'custom-stickers',
    material: 'white-vinyl-removable-semi',
  },

  // ============================================================
  // Unique Custom Products (4) — productData category id is `magnets`.
  // ============================================================
  'Unique Custom Products|Die-Cut Car Magnets|Standard': {
    product: 'magnets',
    format: 'vehicle-magnets',
    material: 'magnets-30mil-matte-vehicle',
  },
  'Unique Custom Products|Die-Cut Fridge Magnets|Standard': {
    product: 'magnets',
    format: 'magnets',
    material: 'magnets-20mil-matte-fridge',
  },
  'Unique Custom Products|Image Transfers|Transfer Decal Permanent Glossy': {
    product: 'magnets',
    format: 'uv-dtf-stickers',
    material: 'uv-dtf-permanent-stickers',
  },
  'Unique Custom Products|Transfers|Transfer Sticker Permanent Glossy': {
    product: 'magnets',
    format: 'uv-dtf-stickers',
    material: 'uv-dtf-permanent-stickers',
  },

  // ============================================================
  // Wearables (3) — productData category id is `iron-ons`.
  // ============================================================
  'Wearables|Badges|Standard': {
    product: 'iron-ons',
    format: 'name-badges',
    material: 'name-badges-styrene-matte-magnetic',
  },
  'Wearables|DTF Heat Transfers|Image Transfer Iron-Ons (DTF)': {
    product: 'iron-ons',
    format: 'dtf-heat-transfers',
    material: 'permanent-image-transfer-dtf',
  },
  'Wearables|Patches|Standard': {
    product: 'iron-ons',
    format: 'emblems-patches',
    material: 'patches-canvas-matte',
  },
};

// Build a case-insensitive lookup map at module-load.
const _CI_INDEX = {};
for (const key of Object.keys(ALIASES)) {
  _CI_INDEX[key.toLowerCase()] = ALIASES[key];
}

/**
 * Look up an explicit alias for the given (category, format, finish)
 * triple. Returns `{product, format, material}` when found, otherwise
 * `null` so the caller can fall back to the heuristic resolver.
 */
export const lookupPricingAlias = (category, format, finish) => {
  if (!category || !format || !finish) return null;
  const key = `${category}|${format}|${finish}`.toLowerCase();
  return _CI_INDEX[key] || null;
};

export default ALIASES;
